/* eslint-disable max-classes-per-file */
import * as React from 'react';
import { SubmissionError } from 'redux-form';
import { diagnostics } from '@blend/ui-common';
import { DataShape } from 'redux-form';
import { RouterState, withRouter } from 'react-router';
import * as queryString from 'query-string';

import InputMap from 'login-components/Common/Inputs/InputMap';
import ErrorMessage from 'login-components/Common/Notifications/ErrorMessage/ErrorMessage';
import { setHref } from 'login-utils/globalWrappers';

import connectedSimpleForm from '../SimpleForm/SimpleForm';
import { get, login } from '../../apis/thirdPartyRecipientsAPI';
import { setDocusignReentryCookie } from './utils';

type Props = RouterState;

enum GetRequestState {
  Requesting,
  Success,
  Error,
}

enum AccessDocumentStatus {
  Success = 'SUCCESS',
  Failure = 'FAILURE',
}

interface State {
  email: string | null;
  verificationHint: string | null;
  getRequestState: GetRequestState;
}

class UserReadableError extends Error {
  // Ignore coverage to account for work around a bug:
  // https://github.com/gotwarlost/istanbul/issues/690
  // istanbul ignore next
  constructor(message: string) {
    super(message);
    Object.setPrototypeOf(this, UserReadableError.prototype);
  }
}

export class ReviewDocuments extends React.Component<Props, State> {
  state = {
    email: null,
    verificationHint: null,
    getRequestState: GetRequestState.Requesting,
  };

  private parseQueryStringParams() {
    const { location } = this.props;
    const { tprId, disclosuresPackageId } = queryString.parse(location.search) as { [key: string]: string };

    if (!tprId || !disclosuresPackageId) {
      throw new Error(
        `Required query string parameters for Review Documents page are missing. tprId: ${tprId}, disclosuresPackageId: ${disclosuresPackageId}.`,
      );
    }

    return { tprId, disclosuresPackageId };
  }

  async componentDidMount() {
    try {
      const { tprId } = this.parseQueryStringParams();
      const { data } = await get(tprId);
      const { email, verificationHint } = data;
      this.setState({
        email,
        verificationHint,
        getRequestState: GetRequestState.Success,
      });
    } catch (e) {
      diagnostics.error(e);
      this.setState({ getRequestState: GetRequestState.Error });
    }
  }

  private async onSubmit(values: DataShape) {
    try {
      const { verificationCode, acceptTermsOfServiceAndEConsent } = values;
      const { tprId, disclosuresPackageId } = this.parseQueryStringParams();

      const { data } = await login({
        id: tprId,
        disclosuresPackageId,
        verificationCode,
        econsentProvided: acceptTermsOfServiceAndEConsent,
      });

      const { status, docusignURL, errorMessage, accessToken } = data;

      if (status === AccessDocumentStatus.Failure) {
        throw new UserReadableError(errorMessage);
      }

      setDocusignReentryCookie({
        partyId: tprId,
        disclosuresPackageId,
        accessToken,
      });
      setHref(docusignURL);
    } catch (e) {
      if (e instanceof UserReadableError) {
        throw new SubmissionError({ _error: e.message });
      }

      diagnostics.error(e);
      throw new SubmissionError({
        _error: 'We experienced a problem retrieving your documents. Please contact your loan team.',
      });
    }
  }

  render() {
    const { getRequestState, email, verificationHint } = this.state;
    const defaultVerificationHint = 'Please enter the verification code provided by your loan team.';

    if (getRequestState === GetRequestState.Error) {
      return <ErrorMessage errorText="No documents found." />;
    }

    if (getRequestState === GetRequestState.Requesting) {
      return <div />;
    }

    const inputs: FormInput[] = [
      {
        ...InputMap.EmailInput,
        identifier: 'email address',
        name: 'email',
        disabled: true,
        label: 'Email address',
        hidePlaceholder: true,
      },
      {
        ...InputMap.SimpleTextInput,
        identifier: 'response',
        label: verificationHint || defaultVerificationHint,
        name: 'verificationCode',
      },
      {
        ...InputMap.RequirementInput,
        acceptedTermsTypes: ['termsOfUse', 'privacyPolicy', 'eConsent'],
        identifier: 'termsAndEConsent',
        name: 'acceptTermsOfServiceAndEConsent',
      },
    ];
    const SimpleForm = connectedSimpleForm(inputs, { email });

    return <SimpleForm inputs={inputs} submitFn={values => this.onSubmit(values)} actionText="Review Documents" />;
  }
}

export default withRouter(ReviewDocuments);
